/*
 * Copyright 2025 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { makeStyles } from '@material-ui/core/styles';
import githubLogo from '../Root/logo/github-logo.png';
import jenkinsLogo from '../Root/logo/jenkins-logo.png';
import sonarqubeLogo from '../Root/logo/sonarqube-logo.svg';
import jiraLogo from '../Root/logo/jira-logo.svg';
import confluenceLogo from '../Root/logo/confluence-logo.svg';
import jfrogLogo from '../Root/logo/jfrog-logo.png';
import { configApiRef, identityApiRef, useApi } from '@backstage/core-plugin-api';
import { useEffect, useState } from 'react';

export const useLogoStyles = makeStyles(theme => ({
  container: {
    margin: theme.spacing(5, 0),
  },
  svg: {
    width: 'auto',
    height: 100,
  },
  path: {
    fill: '#7df3e1',
  },
}));

const size = 24;

export const TOOL_ICONS: Record<string, JSX.Element> = {
  github: <img src={githubLogo} alt="GitHub Logo" style={{ width: size, height: size }} />,
  jenkins: <img src={jenkinsLogo} alt="Jenkins Logo" style={{ width: size, height: size }} />,
  sonarqube: <img src={sonarqubeLogo} alt="SonarQube Logo" style={{ width: size, height: size }} />,
  jira: <img src={jiraLogo} alt="Jira Logo" style={{ width: size, height: size }} />,
  confluence: <img src={confluenceLogo} alt="Confluence Logo" style={{ width: size, height: size }} />,
  jfrog: <img src={jfrogLogo} alt="JFrog Logo" style={{ width: size, height: size }} />,
};

export const useHomeTools = () => {
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);

  const backendUrl = config.getString('backend.baseUrl');
  const [tools, setTools] = useState([]);

  useEffect(() => {
    const fetchTools = async () => {
      const token = (await identityApi.getCredentials()).token;
      const url = `${backendUrl}/api/devops-api-proxy/tools`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save template config');
      }
      const data = await response.json();
      setTools(data);
    }
    fetchTools();
  }, []);

  return tools.map((tool: any) => {
    const key = tool.key;
    return {
      key,
      label: tool.label,
      url: tool.url,
      icon: TOOL_ICONS[key],
    };
  });
};

export const catalogs = [
  {
    id: 'applications',
    title: 'Aplicaciones',
    description: 'Catálogo centralizado de aplicaciones IT de Acciona, con información funcional y técnica.',
    url: '/catalog?filters%5Bkind%5D=application&limit=20&offset=0',
  },
  {
    id: 'projects',
    title: 'Proyectos',
    description: 'Catálogo de proyectos IT de Acciona, incluyendo estado, alcance y responsables.',
    url: '/catalog?filters%5Bkind%5D=project&limit=20&offset=0',
  },
  {
    id: 'apis',
    title: 'APIs Publicadas',
    description: 'Catálogo de APIs IT de Acciona disponibles para integración y consumo interno.',
    url: 'catalog?filters%5Bkind%5D=api&limit=20&offset=0',
  },
  {
    id: 'images-base',
    title: 'Imágenes Base',
    description: 'Repositorio de imágenes base IT de Acciona para entornos y despliegues estandarizados.',
  },
]
